import ExcelJS from 'exceljs';
import { getCollaborators } from '@/features/Collaborators/Services/collaboratorPrivateService';
import { fetchCapabilityCatalog } from '@/features/Collaborators/Data/hooks/useCapabilityCatalogQuery';
import { MONTHLY_FULL_LOAD_HOURS } from '../exportsPage.definitions';
import type {
    DeveloperHoursRow,
    ExportProgressHandler,
    MonthRange,
} from '../exportsPage.types';
import {
    buildDeveloperHoursRows,
    downloadWorkbook,
    formatMonthLabel,
    selectDevelopers,
} from '../utils';
import { fetchAllocationHoursItems } from './allocationsData';

const HOURS_FORMAT = '#,##0.00';

/** Monta a planilha de horas de desenvolvedores a partir das linhas já agregadas. */
function buildWorkbook(rows: DeveloperHoursRow[], range: MonthRange): ExcelJS.Workbook {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Horas Desenvolvedores');

    worksheet.columns = [
        { header: 'Mês', key: 'month', width: 14 },
        { header: 'Desenvolvedores', key: 'developerCount', width: 18 },
        { header: 'Horas Disponíveis', key: 'availableHours', width: 20 },
        { header: 'Horas Alocadas', key: 'allocatedHours', width: 18 },
        { header: 'Horas Academy', key: 'academyHours', width: 18 },
        { header: 'Horas Projetos Internos', key: 'internalProjectHours', width: 24 },
    ];

    worksheet.getRow(1).font = { bold: true };

    rows.forEach((row) => {
        worksheet.addRow(row);
    });

    ['availableHours', 'allocatedHours', 'academyHours', 'internalProjectHours'].forEach((key) => {
        worksheet.getColumn(key).numFmt = HOURS_FORMAT;
    });

    worksheet.getColumn('developerCount').alignment = { horizontal: 'right' };
    worksheet.autoFilter = { from: 'A1', to: 'F1' };
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    worksheet.getCell('A1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text:
                    `Período: ${formatMonthLabel(range.start)} a ${formatMonthLabel(range.end)}.\n\n` +
                    'Considera apenas colaboradores cuja habilidade principal é de um tipo ' +
                    '"Desenvolvedor" (Backend, Frontend, Mobile, etc.).',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    worksheet.getCell('C1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text:
                    `${MONTHLY_FULL_LOAD_HOURS}h x desenvolvedores com vínculo no mês — mesma carga cheia ` +
                    'usada no cálculo de disponibilidade do sistema.\n' +
                    'Quem está desativado sem data de demissão registrada não entra em mês nenhum.',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    worksheet.getCell('D1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text:
                    'Soma das horas/mês das alocações vigentes no mês (diário x21, semanal x4, mensal ' +
                    'direto, pré-definido rateado pela duração).\n' +
                    'A alocação conta no mês se cobrir ao menos um dia dele, sempre com o valor mensal cheio.\n' +
                    'Ficam de fora: canceladas, ausências e aviso prévio.\n\n' +
                    'Academy e Projetos Internos são recortes desta coluna, não parcelas somadas a ela.',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    worksheet.getCell('E1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text: 'Alocações cuja iniciativa contém "academy".',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    worksheet.getCell('F1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text: 'Alocações cuja iniciativa contém "onvue" ou "raro digital".',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    return workbook;
}

/**
 * Exporta as horas mensais dos desenvolvedores no intervalo informado.
 * Retorna quantos desenvolvedores entraram na conta — zero significa que nada
 * foi gerado.
 */
export async function exportDeveloperHours(
    range: MonthRange,
    onProgress?: ExportProgressHandler
): Promise<number> {
    const [collaborators, catalog] = await Promise.all([
        getCollaborators({ orderByField: 'name', orderDirection: 'asc' }),
        fetchCapabilityCatalog(),
    ]);

    const developers = selectDevelopers(collaborators, catalog);
    if (developers.length === 0) {
        return 0;
    }

    const allocations = await fetchAllocationHoursItems(
        developers.map((developer) => developer.id),
        onProgress
    );

    const rows = buildDeveloperHoursRows(developers, allocations, range);
    const workbook = buildWorkbook(rows, range);

    await downloadWorkbook(workbook, `horas-desenvolvedores-${range.start}_${range.end}.xlsx`);

    return developers.length;
}
